import { React, useMemo, useEffect } from "react";
import NavBar from "../components/NavBar/NavBar.jsx";
import Search from "../components/SearchArea/Search.jsx";
import FilteredProductList from "../components/Product/FilteredProductList.jsx";
import useAppStore from "../Store/SearchStore.jsx";
import useScrollToTop from "../Hooks/useScrollToTop";
import RatingFaces from "../components/Rating/Rating.jsx";
import CreatePersona from "../components/Persona/CreatePersona.jsx";
import Loader from "../components/Loader/Loader.jsx";
import { MentionProvider } from "../components/SearchArea/MentionContext.jsx";
import useFavoriteStore from "../Store/FavoriteStore.jsx";
import Popup from "../components/PopUp/PopUpMessage.jsx";
import { useAnimation, AnimatePresence } from "framer-motion";
import ShareButton from "../components/SearchArea/ShareButton.jsx";
import { useScrollAnimation } from "../Hooks/useScrollAnimation.js";
import { SPRING_ANIMATION, ANIMATION_POSITIONS, SLIDE_UP_LARGE_VARIANTS } from "../constants/animations";
import { useLocation } from "react-router-dom";
import useAuthStore from "../Store/AuthStore.jsx";
import PersonaForm from "../components/Persona/PersonaForm.jsx";
import PersonaSelector from "../components/Persona/PersonaSelector.jsx";
import usePersonaStore from "../Store/PersonaStore.jsx";
import { useTranslation } from "react-i18next";
import { getPersonaInitial } from "../Utils/personaUtils.js";
import { AnimatedDiv } from "../components/ui/Motion";
import { useRatingTimer } from "../Hooks/useRatingTimer";
import { ADDITIONAL_CLASSES } from "../constants/searchPageConstants";
import Cookies from "js-cookie";


const SearchPage = () => {
  useScrollToTop();
  const { controls } = useScrollAnimation();
  const ratingControls = useAnimation();
  const location = useLocation();

  // Fetch user personas when component mounts
  useEffect(() => {
    usePersonaStore.getState().fetchUserPersonas();
  }, []);

  // Destructure store values
  const {
    gifts,
    isSearching,
    count,
  } = useAppStore((state) => ({
    gifts: state.gifts,
    isSearching: state.isSearching,
    count: state.count,
  }));

  const { isAuthenticated, signInTime } = useAuthStore();

  // Use custom hook for rating timer
  const timeElapsed = useRatingTimer(signInTime, count);

  // Get persona-related state from PersonaStore
  const {
    showCreatePersonaForUnauthUser,
    generatedPersonaData,
    showPersonaForm,
    showFullPersona,
    handleAvatarClick,
  } = usePersonaStore();

  const { showPopup, popupMessage, popupImageUrl, popupLocation, popupType } =
    useFavoriteStore();

  // Check if should show rating (3 counts)
  const shouldShowRating = useMemo(
    () => count === 3,
    [count]
  );

  // Memoize the share button visibility condition
  const shouldShowShareButton = useMemo(
    () =>
      window.location.pathname.startsWith("/search") &&
      window.location.search.includes("query=") &&
      !shouldShowRating,
    [shouldShowRating]
  );

  const { t } = useTranslation();
  const country = Cookies.get("X-countryName");
  const isNotGermany = !country || country.toLowerCase() !== "germany";

  const translatedGermanyNote = t("career.germanyNote", {
    country: country || t("career.neighborhood")
  });

  return (
    <div className="bg-grey-500">
      <MentionProvider>
        <NavBar />
        {isNotGermany && (
          <div className="bg-orange-50 border-orange-200 border-b text-orange-800 px-4 py-2.5 shadow-sm">
            <div className="max-w-7xl mx-auto flex items-center justify-center">
              <div className="flex items-center">
                <span className="flex p-1.5 rounded-full bg-orange-100 mr-3 shrink-0">
                  <svg className="h-4 w-4 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </span>
                <p className="font-medium text-sm sm:text-base text-orange-900 leading-tight">
                  {translatedGermanyNote}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Product has been added PopUp */}
        <AnimatePresence>
          {showPopup && (
            <Popup
              message={popupMessage}
              imageUrl={popupImageUrl}
              location={popupLocation}
              type={popupType}
            />
          )}
        </AnimatePresence>

        {/* Show Products or Loader */}
        {isSearching ? (
          <div className="flex justify-center items-center h-screen">
            <Loader />
          </div>
        ) : (
          <div>
            <FilteredProductList gifts={gifts} />
          </div>
        )}

        {/* The Search Bar */}
        <div className="fixed bottom-7 w-full gap-1 flex flex-col justify-end z-20">
          {" "}
          {/* Increased z-index to ensure it's on top */}
          <AnimatedDiv
            className={`${ADDITIONAL_CLASSES.CONTAINER} relative z-2`}
            initial={ANIMATION_POSITIONS.visible}
            animate={controls}
            transition={SPRING_ANIMATION}
          >
            <AnimatedDiv
              className={`${ADDITIONAL_CLASSES.SHARE_BUTTON_CONTAINER} relative z-1`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{
                opacity: { type: "spring", stiffness: 400, damping: 30 },
                default: { type: "spring", stiffness: 400, damping: 30 },
              }}
            >
              {shouldShowShareButton && <ShareButton />}
            </AnimatedDiv>
          </AnimatedDiv>
          {/* Show Persona Form when requested */}
          {showPersonaForm && (

            <PersonaForm />
          )}
          {/* Show Persona Selector (includes suggested persona for unauth users) */}
          {(isAuthenticated || showCreatePersonaForUnauthUser) && !showPersonaForm && (
            <AnimatedDiv
              className={ADDITIONAL_CLASSES.PERSONA_CONTAINER}
              initial="hidden"
              animate={ratingControls}
              variants={SLIDE_UP_LARGE_VARIANTS}
              onViewportEnter={() => ratingControls.start("visible")}
            >
              <PersonaSelector />
            </AnimatedDiv>
          )}
          {shouldShowRating && (
            <AnimatedDiv
              className={`${ADDITIONAL_CLASSES.CONTAINER} relative z-10`}
              initial="hidden"
              animate={ratingControls}
              variants={SLIDE_UP_LARGE_VARIANTS}
              onViewportEnter={() => ratingControls.start("visible")}
            >
              <div >
                <RatingFaces />
              </div>
            </AnimatedDiv>
          )}
          <Search pageType="searchPage" />
        </div>
      </MentionProvider>
    </div>
  );
};

export default SearchPage;
